import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { useProductContext } from 'src/areas/product/components/product-context';
import { useListContext } from '../../../list-context';

import { IconHeartOutlined } from '@prisjakt/ui/icon/heart-outlined';
import { IconHeartFilled } from '@prisjakt/ui/icon/heart-filled';
import { IconNotificationFilled } from '@prisjakt/ui/icon/notification-filled';
import { Dropdown, DropdownContent } from '@prisjakt/ui/dropdown';
import { ButtonIcon, ButtonSize, ButtonType } from '@prisjakt/ui/button';
import { TextButton } from '@prisjakt/ui/button/variants';
import { Direction } from '@prisjakt/ui';
import LoginPromoModal from 'src/components/modal/login-promo';
import {
  openAlerts,
  removeAlertRequest,
} from 'src/areas/sidebar/actions/alert'; // Should not depend on other areas
import {
  selectList,
  addListItemRequest,
  addItemToMyProductsRequest,
} from 'src/areas/sidebar/actions/lists'; // Should not depend on other areas
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';
import { SaveToListButton } from './buttons/save-button';
import { CompareButton } from './buttons/compare-button';
import { AlertButton } from './buttons/alert-button';

import type { AppState } from 'src/reducers';

import { useOnAction } from '../../../utils';

import type { UserProductAlert } from '../../../../../../sidebar/types';
import type { Me } from '../../../types';

import { StyledButtonText, StyledTooltip } from './shared-styles';
import {
  StyledActionButton,
  StyledDropdownItem,
  StyledDropdownToggleButton,
  StyledProductActionButtonWrapper,
  StyledSavedListWrapper,
} from './styles';

type Props = {
  className?: string;
};

export const ActionButtonsDesktop = ({ className }: Props) => {
  const { t } = useAppContext();
  const { fetchMoreLists, moreLists, pageInfo } = useListContext();
  const { product } = useProductContext();

  const dispatch = useDispatch();

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const isAuthenticated = useIsAuthenticated();
  const userProductAlerts = useSelector(
    (state: AppState) => state.areas.sidebar.alerts.products
  );
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );

  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const toggleLoginPromo = () => setLoginPromoState(!showLoginPromo);

  const onAction = useOnAction();

  const addToList = () => {
    onAction('list-add');
    dispatch(addListItemRequest(product.id, product.name, true));

    setAddedToListState(true);
  };
  const latestLists =
    me?.lists?.nodes
      ?.filter(list => deletedLists[list.id] !== true)
      .filter(list => !list.inventory)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      }) ?? [];

  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );

  const alertId = userAlert && userAlert.alertId;

  const removeAlert = () => {
    if (!alertId) return;

    onAction('alert-remove');
    dispatch(openAlerts());
    dispatch(removeAlertRequest(alertId, product.id));
  };

  const editAlert = () => {
    dispatch(openAlerts());
    onAction('alert-create');
  };

  const addToMyProducts = () => {
    onAction('list-add');
    dispatch(addItemToMyProductsRequest(product.id, product.name, true));
    setAddedToListState(true);
  };

  const isBookmarked = alertId && isAuthenticated;

  const onAlertClickHandler = isBookmarked
    ? undefined
    : () => {
        onAction('alert-create');
        return false;
      };

  return (
    <StyledProductActionButtonWrapper className={className}>
      <StyledActionButton data-test="SaveToListButton">
        {isAuthenticated ? (
          <StyledTooltip
            title={t('product', 'action-button.list-tooltip')}
            placement={Direction.Down}
            zIndex={1}
          >
            <Dropdown>
              <StyledDropdownToggleButton
                ariaLabel={t('common', 'button.save')}
                type={ButtonType.TONAL}
                size={ButtonSize.SMALL}
              >
                <ButtonIcon>
                  {addedToList ? <IconHeartFilled /> : <IconHeartOutlined />}
                </ButtonIcon>
                <StyledButtonText>
                  {t('common', 'button.save')}
                </StyledButtonText>
              </StyledDropdownToggleButton>
              <DropdownContent>
                <StyledSavedListWrapper>
                  <StyledDropdownItem onClick={addToMyProducts}>
                    {t('lists', 'my-products.title')}
                  </StyledDropdownItem>
                  {[...latestLists, ...(moreLists || [])].map(list => (
                    <StyledDropdownItem
                      onClick={() => {
                        dispatch(selectList(list.id));
                        addToList();
                      }}
                      key={list.id}
                    >
                      {list.name}
                    </StyledDropdownItem>
                  ))}

                  {pageInfo?.hasNext && (
                    <TextButton onClick={fetchMoreLists}>
                      {t('common', 'button.show-more')}
                    </TextButton>
                  )}
                </StyledSavedListWrapper>
              </DropdownContent>
            </Dropdown>
          </StyledTooltip>
        ) : (
          <>
            {showLoginPromo && (
              <LoginPromoModal
                isOpen={showLoginPromo}
                onRequestClose={toggleLoginPromo}
              />
            )}
            <SaveToListButton
              isActive={addedToList}
              onClick={toggleLoginPromo}
            />
          </>
        )}
      </StyledActionButton>

      <StyledActionButton data-test="CreateAlertButton">
        {isBookmarked ? (
          <StyledTooltip
            placement={Direction.Down}
            title={t('product', 'action-button.alert-tooltip')}
            zIndex={1}
          >
            <Dropdown>
              <StyledDropdownToggleButton
                ariaLabel={t('product', 'alert.create-button')}
                type={ButtonType.TONAL}
                size={ButtonSize.SMALL}
              >
                <ButtonIcon>
                  <IconNotificationFilled />
                </ButtonIcon>
                <StyledButtonText>
                  {t('product', 'alert.create-button')}
                </StyledButtonText>
              </StyledDropdownToggleButton>
              <DropdownContent>
                <StyledDropdownItem onClick={removeAlert}>
                  {t('product', 'alert.delete-button')}
                </StyledDropdownItem>

                <StyledDropdownItem onClick={editAlert}>
                  {t('product', 'alert.edit-button')}
                </StyledDropdownItem>
              </DropdownContent>
            </Dropdown>
          </StyledTooltip>
        ) : (
          <AlertButton isActive={false} onClick={onAlertClickHandler} />
        )}
      </StyledActionButton>

      <StyledActionButton data-test="CompareButton">
        <CompareButton />
      </StyledActionButton>
    </StyledProductActionButtonWrapper>
  );
};
